import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Filters garnlinje rows to the selected size and quality, and adds the
 * visible ones to the cart in one request.
 *
 * The rows are all rendered server-side and hidden with [hidden] rather
 * than re-fetched: the whole set is at most a few dozen rows, and a
 * round-trip per selector change would be slower and would break with JS
 * disabled. Without JS every row stays visible, which is a correct if
 * unfiltered reading of the same data.
 */
class OppskriftGarnlinjer extends Component {
  // `addAll` is deliberately NOT required. When the pattern has a garnpakke
  // in stock, the Liquid renders a link to that kit in place of the add-all
  // button, so the ref is legitimately absent. It used to be required, and
  // the missing ref threw in connectedCallback BEFORE #applyFilter ran —
  // which left every size and quality visible at once, so the page read as
  // if the yarn list had duplicated itself. Filtering the rows is this
  // component's core job and must not depend on the button existing.
  requiredRefs = ['list', 'status'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onFilterChange);
    this.refs.addAll?.addEventListener('click', this.#onAddAll);
    this.#applyFilter();
  }

  /**
   * The Section Rendering API re-renders this block whenever a setting is
   * changed in the theme editor. `Component.updatedCallback` (component.js)
   * re-binds refs but knows nothing about our filter state, so without this
   * every row reappears the moment the merchant edits a label — the block
   * looks broken exactly while she is working on it.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyFilter();
  }

  /**
   * Narrow each axis to the values still reachable, and return what is
   * selected once that narrowing is done.
   *
   * The axes are read off the rendered fieldsets rather than named here.
   * The Liquid omits a fieldset whose axis has one value or none, so a
   * missing axis simply does not filter — and a fourth axis would need
   * Liquid only.
   *
   * Cascading is DIRECTIONAL: an axis is narrowed only by the axes ABOVE
   * it in document order (Størrelse, then Kvalitet, then Farge). Making it
   * symmetric looks tidier and is wrong — Bøttehatt Oppegård's two yarn
   * qualities share almost no colour names, so narrowing Kvalitet by the
   * current Farge would hide the quality the shopper is trying to switch
   * to, and the selector they are reaching for disappears as they reach.
   * Direction is what makes "pick a quality, then a colour in it" work.
   *
   * Re-selecting matters as much as hiding. Measured live 2026-08-18:
   * the Farge fieldset offered all 50 colours across both qualities, so
   * choosing Øko bomull while a Bomull Sport colour was checked rendered
   * ZERO yarn rows — a blank section with no explanation. Hiding the
   * unreachable options alone would leave that same dead end, just with a
   * shorter list; the invalid choice has to be moved to a valid one.
   */
  #cascadeFilters() {
    const rows = [...this.refs.list.querySelectorAll('.garnlinjer__row')];
    const selection = {};

    for (const fieldset of this.querySelectorAll('fieldset[data-filter]')) {
      const axis = fieldset.dataset.filter;
      // Rows still reachable given every axis already decided above.
      const reachable = rows.filter((row) =>
        Object.entries(selection).every(
          ([decided, value]) => value === null || row.dataset[decided] === value
        )
      );
      const available = new Set(reachable.map((row) => row.dataset[axis]));
      // A selector left with one usable option offers no choice -- the
      // Liquid never renders one -- so the whole fieldset goes too.
      fieldset.hidden = false;
      if (axis === 'kvalitet' && this.#dropDeadQualities(available, reachable)) {
        fieldset.hidden = available.size < 2;
      }

      // Two controls render this axis, and which one is Liquid's call:
      // above a threshold of values it emits a <select>, below it radios
      // (see the block's farge fieldset). Both narrow the same way, so
      // the branch is here rather than in two copies of the loop.
      let checked = null;
      let firstAvailable = null;
      const select = fieldset.querySelector('select');

      if (select) {
        for (const option of select.options) {
          const usable = available.has(option.value);
          // `hidden` AND `disabled`: hidden on an <option> is honoured by
          // every current browser but has a longer history of being
          // ignored than the attribute on an element, and a disabled
          // option cannot be chosen even where it still renders. The
          // belt-and-braces costs one line and removes a whole class of
          // "it works here but not there".
          option.hidden = !usable;
          option.disabled = !usable;
          if (usable && firstAvailable === null) firstAvailable = option.value;
          if (usable && option.selected) checked = option.value;
        }
        if (checked === null && firstAvailable !== null) {
          select.value = firstAvailable;
          checked = firstAvailable;
        }
      } else {
        for (const label of fieldset.querySelectorAll('label')) {
          const input = label.querySelector('input');
          const usable = available.has(input.value);
          label.hidden = !usable;
          if (usable && firstAvailable === null) firstAvailable = input;
          if (usable && input.checked) checked = input.value;
        }
        if (checked === null && firstAvailable !== null) {
          firstAvailable.checked = true;
          checked = firstAvailable.value;
        }
      }
      // No reachable value at all leaves this axis null, i.e. not
      // filtering — the same meaning a one-option axis has, so the rows
      // below stay visible rather than the section going blank.
      selection[axis] = checked;
    }
    return selection;
  }

  /**
   * Drop a quality the shopper cannot buy at all from the selector.
   *
   * With a kit per quality on the page, a quality that has NO kit for sale
   * falls back to the add-all button -- and when any of its reachable yarn
   * is sold out that button is disabled, so choosing it leads nowhere.
   * Measured on the Viking patterns 2026-09-23: Bomull Sport's kit paused,
   * 307 Marine sold out, and the only action left was a greyed button.
   *
   * Only when kit buttons carry qualities (the per-quality layout), and
   * never down to nothing: if every quality would go, none does, the same
   * "never a dead page" rule #applyKitFilter follows.
   */
  #dropDeadQualities(available, reachable) {
    const kitQualities = new Set(
      [...this.querySelectorAll('.garnlinjer__kits .garnlinjer__add')]
        .map((kit) => kit.dataset.kvalitet)
        .filter(Boolean)
    );
    if (kitQualities.size === 0 || !this.refs.addAll) return false;
    const dead = [...available].filter(
      (value) =>
        !kitQualities.has(value) &&
        reachable.some((row) => row.dataset.kvalitet === value && 'unavailable' in row.dataset)
    );
    if (dead.length === 0 || dead.length === available.size) return false;
    for (const value of dead) available.delete(value);
    return true;
  }

  #visibleRows() {
    return [...this.refs.list.querySelectorAll('.garnlinjer__row')].filter(
      (row) => !row.hidden
    );
  }

  #onFilterChange = () => this.#applyFilter();

  #applyFilter() {
    // A null selection means that axis has one value or none, so it does
    // not filter — matching the Liquid, which omits a one-option selector.
    const selection = this.#cascadeFilters();
    for (const row of this.refs.list.querySelectorAll('.garnlinjer__row')) {
      row.hidden = Object.entries(selection).some(
        ([axis, value]) => value !== null && row.dataset[axis] !== value
      );
    }
    this.#applyKitFilter(selection);
    this.refs.status.textContent = '';
    if (this.refs.addAll) {
      // Blocked when ANY yarn in the selected combination is sold out, not
      // only when every one is. The rows are a recipe, not a menu: adding
      // the two colours that happen to be in stock sells someone
      // two-thirds of a hat they cannot finish, and the omission is
      // invisible in the cart. Was `=== 0`, which blocked only the
      // all-sold-out case — measured 2026-08-07, three of the four
      // qualities on vi-3-jer were part-sold-out and addable.
      const rows = this.#visibleRows();
      const soldOut = rows.filter((row) => 'unavailable' in row.dataset);
      this.refs.addAll.disabled = rows.length === 0 || soldOut.length > 0;
      // Name the yarn: "Noe av garnet er utsolgt: 307 Marine" says what to
      // wait for, where the bare label only said something was wrong. The
      // colour is the part of the row name after its last " · " (the
      // product title precedes it); the label stays the merchant's own
      // setting, so the sentence still translates.
      const names = soldOut.map((row) => {
        const text = row.querySelector('.garnlinjer__name')?.textContent.trim() ?? '';
        return text.split(' · ').pop();
      });
      this.refs.status.textContent = soldOut.length > 0
        ? `${this.dataset.blockedLabel ?? ''}: ${names.join(', ')}`
        : '';
    }
  }

  /**
   * Show only the garnpakke matching the selected quality.
   *
   * A pattern carrying two yarn qualities renders one "Kjøp garnpakke"
   * button per quality, directly under a Kvalitet selector that filters
   * the yarn rows. Leaving both buttons up meant one of them contradicted
   * the shopper's own choice — pick Øko bomull and a Bomull Sport button
   * still sat there offering the other quality.
   *
   * `data-kvalitet` comes from the kit's `garnpakke.kvalitet` metafield,
   * NOT from its title. A kit with no value gets an empty attribute and is
   * never hidden: the pattern may be single-quality, or the kit may simply
   * not have been reconciled yet, and in both cases hiding it would remove
   * the only way to buy it. Same reasoning as the row filter treating a
   * null selection as "this axis does not filter".
   *
   * `.button[hidden]` is already `display: none` in base.css, so setting
   * `hidden` is enough — no extra rule in this block's stylesheet.
   */
  #applyKitFilter(selection) {
    const kits = [...this.querySelectorAll('.garnlinjer__kits .garnlinjer__add')];
    if (kits.length === 0) {
      // No kit to offer: add-all is the only way to buy, so it must never
      // be left hidden by an earlier pass.
      if (this.refs.addAll) this.refs.addAll.hidden = false;
      return;
    }

    // A null selection means the axis has one value or none, so it does not
    // filter — but it must still UNHIDE, not return early. An early return
    // leaves whatever the previous pass hid still hidden, which is invisible
    // today (a missing fieldset reads null on every call, so nothing is ever
    // hidden to begin with) and would become a stale-state bug the moment an
    // axis can appear or disappear between passes.
    const hidden = (kit) =>
      selection.kvalitet != null &&
      Boolean(kit.dataset.kvalitet) &&
      kit.dataset.kvalitet !== selection.kvalitet;

    // Hiding EVERY kit would leave the shopper no way to buy at all: this
    // branch renders no add-all button to fall back to, so an empty result
    // is a dead end rather than a filtered one. Reachable when a pattern
    // declares more qualities than it has buyable kits — three qualities in
    // the metaobject sets, two kits in stock, pick the third. Showing all of
    // them is a worse match but still an offer; showing none is a broken page.
    //
    // Unless an add-all button is rendered beside the kits: the Liquid does
    // that whenever a Kvalitet selector is on the page, precisely so a
    // quality with no kit for sale (the paused Viking Bomull Sport kits,
    // 2026-09-23) offers ITS OWN yarn rather than a kit in the other
    // quality. Then every kit hides and add-all shows; otherwise add-all
    // hides, so there is only ever one way to buy the selection.
    const addAll = this.refs.addAll;
    if (kits.every(hidden)) {
      if (addAll) {
        for (const kit of kits) kit.hidden = true;
        addAll.hidden = false;
        return;
      }
      for (const kit of kits) kit.hidden = false;
      return;
    }
    for (const kit of kits) kit.hidden = hidden(kit);
    if (addAll) addAll.hidden = true;
  }

  #onAddAll = async () => {
    const rows = this.#visibleRows();
    // Same rule as the disabled state above: all or nothing.
    if (rows.length === 0 || rows.some((row) => 'unavailable' in row.dataset)) return;

    this.refs.addAll.disabled = true;
    this.refs.status.textContent = '';

    const lines = rows.map((row) => ({
      merchandiseId: Number(row.dataset.variantId),
      quantity: Number(row.dataset.antall),
    }));

    // Dispatch immediately with a still-pending promise, then resolve or
    // reject it once the fetch settles — the house pattern (product-form.js,
    // component-cart-items.js). Dispatching only on success would make a
    // failed add invisible to every other cart-aware component on the page
    // (cart-icon.js, header-actions.js, sticky-add-to-cart.js).
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(this.refs.addAll.dataset.addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: lines.map(({ merchandiseId, quantity }) => ({ id: merchandiseId, quantity })),
        }),
      });
      if (!response.ok) {
        // Shopify returns the failing line's reason in `description`. Throw
        // rather than reject here: fetch() itself can also reject before a
        // response ever exists (offline, DNS, CORS, abort), and that path
        // shares this same outer catch, so settling `deferred` there — once,
        // for every way the add can fail — keeps a single reject call site
        // per outcome instead of one here and a duplicate guarded by a flag.
        const body = await response.json().catch(() => ({}));
        throw new Error(body.description || response.statusText);
      }

      // The POST succeeded — the yarn is in the cart. Everything from here
      // on is cosmetic bubble-count plumbing and must never turn a
      // successful add into a user-facing failure (product-form.js keeps
      // its "added" feedback independent of #refreshCart()'s outcome the
      // same way — a refresh failure there only rejects the event promise).
      this.refs.status.textContent = '';

      // /cart/add.js returns only the added line items, not cart totals —
      // fetch the cart so the bubble knows the new count, same #refreshCart
      // pattern as product-form.js. Not awaited: a slow or failed refresh
      // must not hold the button disabled or read as an add failure.
      fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      })
        .then((r) => r.json())
        .then((ajaxCart) => {
          deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart) });
        })
        .catch((error) => {
          // The add succeeded; only the cosmetic refresh failed. Reject the
          // event promise (other listeners' problem) — never surface this
          // as status text, or a real success reads as a failure.
          deferred.reject(error);
        });
    } catch (error) {
      // Single settle point for the "add itself failed" outcome: a bad
      // response thrown above, and a network-level failure where fetch()
      // rejects before a response ever exists (offline, DNS, CORS, abort),
      // both land here exactly once — matching the one-.catch() discipline
      // in component-cart-items.js (:288-290).
      deferred.reject(error);
      this.refs.status.textContent = error.message;
    } finally {
      this.refs.addAll.disabled = false;
    }
  };
}

if (!customElements.get('oppskrift-garnlinjer')) {
  customElements.define('oppskrift-garnlinjer', OppskriftGarnlinjer);
}
